import React, { useState } from 'react';
import { EliceButton } from '@elicecontents/content-ui';
import {
  EliceAudioPlayer,
  ReadyRecord,
  Recording,
} from '@elicecontents/content-ui';
import { Button, Stack, styled, Typography } from '@mui/material';

import { SoundVisualizer } from '../sound-visualizer';
import {
  useInitRecorder,
  useRecorderAction,
  useRecorderInnerState,
  RecorderProvider,
} from './recorderStore';

import type { SoundVisualizerVariant } from '@elicecontents/content-ui';
import type { SxProps } from '@mui/material';
import type { AudioStep } from './recorderStore';

export type RecorderStep = 'ready' | 'recording' | 'pause' | 'waiting';
export type RecorderType = 'pause' | 'waiting' | 'unsubmit' | null;

export interface EliceRecorderStepProps {
  onAudioReady: (file: File) => void;
  onTransform?: () => void;
  type?: RecorderType[];
  visualType?: SoundVisualizerVariant;
  isReadyVisual?: boolean;
  onRecord?: () => void;
  onResume?: () => void;
  onPause?: () => void;
  defaultBarColor?: string;
  closeRecorder?: () => void;
  onTranscribingChange?: (value: boolean) => void;
  isLoadingMessage?: boolean;
  forcedStep?: RecorderStep;
  sx?: SxProps;
  onRecordStep?: (step: AudioStep) => void;
  initFile?: File;
  disabled?: boolean;
}

export const EliceRecorderBlock = ({
  onAudioReady,
  onTransform,
  visualType = 'bar',
  onRecord,
  type = [],
  isReadyVisual = false,
  onResume,
  onPause,
  onTranscribingChange,
  forcedStep,
  closeRecorder,
  defaultBarColor,
  sx,
  onRecordStep,
  initFile,
  disabled = false,
}: EliceRecorderStepProps) => {
  useInitRecorder({
    step: forcedStep ?? 'ready',
    isRecording: false,
    file: initFile ?? null,
  });
  const { startRecording, stopRecording, pauseRecording, resumeRecording } =
    useRecorderAction();
  const recorderState = useRecorderInnerState();
  const [audioUrl, setAudioUrl] = useState<string>('');
  const currentStep = recorderState.step;

  const handleStart = async () => {
    onTranscribingChange?.(true);
    onRecord?.();

    await startRecording(file => {
      onAudioReady(file);
      const url = URL.createObjectURL(file);
      setAudioUrl(url);
    });
  };

  const firstTimeInit = React.useRef(false);

  React.useEffect(() => {
    if (initFile) {
      const url = URL.createObjectURL(initFile);
      setAudioUrl(url);
      firstTimeInit.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initFile]);

  const handlePause = () => {
    onPause?.();
    pauseRecording();
  };

  const handleResume = () => {
    onResume?.();
    resumeRecording();
  };

  const handleStop = () => {
    stopRecording(false);
    onTranscribingChange?.(false);
  };

  const handleSubmit = () => {
    onTransform?.();
    closeRecorder?.();
  };

  const handleOnlySubmit = () => {
    stopRecording(true);
    onTranscribingChange?.(false);
    onTransform?.();
    closeRecorder?.();
  };

  React.useEffect(() => {
    onRecordStep?.(currentStep);
  }, [currentStep, onRecordStep]);

  return (
    <StyledVisualizerBox sx={sx}>
      {currentStep === 'ready' && (
        <>
          {!isReadyVisual && (
            <SoundVisualizer
              analyser={recorderState.analyzer}
              variant={visualType}
              bgColor="transparent"
              defaultBarColor={defaultBarColor}
            />
          )}
          <StyledRecorderButton onClick={handleStart} step={currentStep}>
            <ReadyRecord />
            <Typography variant="subtitle2">녹음 시작</Typography>
          </StyledRecorderButton>
        </>
      )}
      {currentStep === 'recording' && (
        <>
          <SoundVisualizer
            analyser={recorderState.analyzer}
            variant={visualType}
            defaultBarColor={defaultBarColor}
            bgColor="transparent"
          />
          <StyledVisualizerBoxButtonContainer>
            {type.includes('pause') && (
              <StyledPauseButton onClick={handlePause}>
                <Recording color="#485EAD" squareColor="#0A379B" />
                <Typography variant="subtitle2">일시 정지</Typography>
              </StyledPauseButton>
            )}
            <StyledRecorderButton
              onClick={type.length !== 0 ? handleStop : handleOnlySubmit}
              step={currentStep}
            >
              <Recording />
              <Typography variant="subtitle2">녹음 완료</Typography>
            </StyledRecorderButton>
          </StyledVisualizerBoxButtonContainer>
        </>
      )}
      {currentStep === 'pause' && (
        <>
          <SoundVisualizer
            analyser={null}
            variant={visualType}
            bgColor="transparent"
          />
          <StyledVisualizerBoxButtonContainer>
            {type.includes('pause') && (
              <StyledPauseButton onClick={handleResume}>
                <Recording color="#485EAD" squareColor="#0A379B" />
                <Typography variant="subtitle2">이어서 녹음</Typography>
              </StyledPauseButton>
            )}
            <StyledRecorderButton onClick={handleStop} step={currentStep}>
              <Recording />
              <Typography variant="subtitle2">녹음 완료</Typography>
            </StyledRecorderButton>
          </StyledVisualizerBoxButtonContainer>
        </>
      )}
      {currentStep === 'waiting' && type.includes('waiting') && (
        <>
          {audioUrl && <EliceAudioPlayer src={audioUrl} />}
          <StyledVisualizerBoxButtonContainer>
            <StyledRecorderButton
              disabled={disabled}
              onClick={handleStart}
              step={currentStep}
            >
              <ReadyRecord />
              <Typography variant="subtitle2">다시 녹음</Typography>
            </StyledRecorderButton>
            {!type.includes('unsubmit') && (
              <EliceButton disabled={disabled} onClick={handleSubmit}>
                <Typography variant="subtitle2">완료</Typography>
              </EliceButton>
            )}
          </StyledVisualizerBoxButtonContainer>
        </>
      )}
    </StyledVisualizerBox>
  );
};

const StyledVisualizerBox = styled(Stack)`
  width: 100%;
  font-size: 20px;
  height: 10.6em;
  background-color: #fff;
  border-radius: 1.2em;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1.6em;
`;

const StyledVisualizerBoxButtonContainer = styled(Stack)`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 1.7em;
  justify-content: center;
  align-items: center;
`;

const StyledRecorderButtonDefault = styled(Button)`
  display: flex;
  width: 16em;
  border: none;
  height: 2.75em;
  padding: 0.6em 1em 0.6em 0.85em;
  justify-content: center;
  align-items: center;
  gap: 0.4em;
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 0.8em;
`;

const StyledRecorderButton = styled(StyledRecorderButtonDefault) <{
  step: RecorderStep;
}>`
  color: ${({ step }) =>
    step === 'ready' || step === 'waiting' ? '#fff' : '#FF5D58'};
  background: ${({ step }) =>
    step === 'ready' || step === 'waiting' ? '#FF5D58' : '#FBE0E0'};

  &:hover {
    background: #FFB1AF !important;
    color: #FF5D58 !important;
  }
`;

const StyledPauseButton = styled(StyledRecorderButtonDefault)`
  color: #0a379b;
  background: #bec4e1;
  &:hover {
    color: #bec4e1;
    background-color: #0a379b;
  }
`;

// EliceRecorder는 내부적으로 RecorderProvider를 제공
export const EliceRecorder = (props: EliceRecorderStepProps) => {
  return (
    <RecorderProvider>
      <EliceRecorderBlock {...props} />
    </RecorderProvider>
  );
};
